import { css } from "@emotion/css";
import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { Note, Text } from "~/components/form";
import { ConfigWizardHelpLayout } from "./ConfigWizardHelpLayout";
import { ConfigWizardStatusBar } from "./ConfigWizardStatusBar";
import type { BranchConfig, BranchInputMode } from "./EphemeralEnvironmentsConfigWizard";
import runbookBranchInfoImage from "./runbook-branch-info.svg";
interface BranchHelpProps {
    defaultBranchName?: string;
}
export function BranchHelp({ defaultBranchName }: BranchHelpProps) {
    if (!defaultBranchName) {
        throw new Error("Default branch name is required.");
    }
    return (<ConfigWizardHelpLayout title="New runbooks">
            <p>
                When you create a new runbook, Octopus stores the changes in Git. Octopus will commit the changes to a new branch based on your default branch (i.e. <code>{defaultBranchName}</code>).
            </p>
            <p>To run your provisioning and deprovisioning runbooks from another branch, remember to merge this runbook configuration to that branch.</p>
        </ConfigWizardHelpLayout>);
}
export const branchHelpImage = {
    src: runbookBranchInfoImage,
    altText: "Graphic of a commit on a new branch creating a runbook on the new branch, and the same commit on the default branch creating the same runbook on the default branch.",
};
interface BranchContentProps {
    defaultBranchName?: string;
    isDefaultBranchProtected?: boolean;
    branchConfig?: BranchConfig;
    setBranchConfig: (config: BranchConfig) => void;
}
export function BranchContent({ defaultBranchName, isDefaultBranchProtected, branchConfig, setBranchConfig }: BranchContentProps) {
    if (!defaultBranchName) {
        throw new Error("Default branch name is required.");
    }
    if (!branchConfig) {
        throw new Error("Branch configuration is required.");
    }
    function handleRadioChange(value: BranchInputMode) {
        if (value === "Default") {
            setBranchConfig({ branch: defaultBranchName, action: "Default" });
            return;
        }
        setBranchConfig({ branch: "", action: "Create" });
    }
    return (<div>
            <ConfigWizardStatusBar activeStage="Branch" isCommittingRunbooksToNewBranch/>
            <h3>Choose a branch for your new runbooks</h3>
            <RadioButtonGroup value={branchConfig.action} onChange={(value) => handleRadioChange(value)}>
                <RadioButton key="default" label={<>
                            Commit to default branch <code>{defaultBranchName}</code>
                        </>} value="Default" disabled={isDefaultBranchProtected}/>
                {isDefaultBranchProtected && <Note>The default branch is protected</Note>}
                <RadioButton key="create" label="Create a new branch" value="Create"/>
                {branchConfig.action == "Create" && <AddBranchTextInput defaultBranchName={defaultBranchName} branchConfig={branchConfig} setBranchConfig={setBranchConfig}/>}
            </RadioButtonGroup>
        </div>);
}
interface AddBranchTextInputProps {
    defaultBranchName: string;
    branchConfig: BranchConfig;
    setBranchConfig: (config: BranchConfig) => void;
}
function AddBranchTextInput({ defaultBranchName, branchConfig, setBranchConfig }: AddBranchTextInputProps) {
    const styles = {
        inputsWidth: css({
            maxWidth: 400,
            width: "100%",
        }),
    };
    return (<div className={styles.inputsWidth}>
            <Text label="New branch name" value={branchConfig.branch || ""} onChange={(value) => setBranchConfig({ branch: value, action: "Create" })} helperText={<>
                        The new branch will be based on the default branch (<code>{defaultBranchName}</code>) in your repository
                    </>}/>
        </div>);
}
